import React from "react";
import { Button, CircularProgress, Tooltip, useTheme } from "@mui/material";
import { FaRegFileExcel } from "react-icons/fa6";

/**
 * Download this table, with the filters currently applied.
 *
 * One button for every report screen, because the four it replaces had each
 * been written separately and no two behaved the same: two were commented
 * out, one was wired to an empty function, and the one that worked had no
 * button at all. Anything that is written five times ends up with five
 * behaviours.
 *
 * Deliberately has nothing to choose. The filters are the ones already on
 * the screen, so the file is what the person is looking at -- that is the
 * whole request, and a modal asking for them again is what it replaces.
 */
const ExportButton: React.FC<{
  onExport: () => void;
  exporting?: boolean;
  /** Greyed out when there is nothing on screen to export. */
  disabled?: boolean;
  tooltip?: string;
}> = ({
  onExport,
  exporting = false,
  disabled = false,
  tooltip = "Download this table, with the filters currently applied",
}) => {
  const theme = useTheme();

  return (
    // A disabled MUI button fires no events, so the tooltip needs a wrapper
    // that can -- otherwise it silently stops appearing exactly when the
    // person most wants to know why the button will not work.
    <Tooltip title={exporting ? "Preparing the file…" : tooltip}>
      <span>
        <Button
          color="success"
          size="medium"
          sx={{
            height: 34,
            textTransform: "capitalize",
            boxShadow: theme.shadows[1],
            whiteSpace: "nowrap",
          }}
          variant="outlined"
          disabled={disabled || exporting}
          startIcon={
            exporting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <FaRegFileExcel size={15} />
            )
          }
          onClick={onExport}
        >
          &nbsp;<span>{exporting ? "Exporting" : "Export"}</span>
        </Button>
      </span>
    </Tooltip>
  );
};

export default ExportButton;
